import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Conservative scroll management hook for non-chat routes only
 * This ensures users land at the top of pages when navigating, but doesn't interfere with chat
 */
export function useScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousLocation = useRef(location);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // CRITICAL: Early exit for chat routes - never interfere with chat scroll behavior
    if (
      location.pathname.startsWith("/chat") ||
      location.pathname.startsWith("/projects")
    ) {
      return;
    }

    // Disable browser's automatic scroll restoration for non-chat routes
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = "manual";
    }
  }, [location.pathname]);

  useEffect(() => {
    // CRITICAL: Early exit for chat routes - never interfere with chat scroll behavior
    if (
      location.pathname.startsWith("/chat") ||
      location.pathname.startsWith("/projects")
    ) {
      return;
    }

    const scrollToTop = () => {
      // Multiple methods to ensure scroll to top works consistently
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });

      // Fallback for different scroll containers
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
        document.documentElement.scrollLeft = 0;
      }

      if (document.body) {
        document.body.scrollTop = 0;
        document.body.scrollLeft = 0;
      }

      // Additional fallback for any scroll containers (but avoid chat containers)
      const scrollableElements = document.querySelectorAll(
        "[data-scroll-container]:not([data-chat-container])"
      );
      scrollableElements.forEach((element) => {
        element.scrollTop = 0;
        element.scrollLeft = 0;
      });
    };

    const handleScrollToTop = () => {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        scrollToTop();

        // Double-check after a short delay for stubborn cases
        setTimeout(scrollToTop, 10);
      });
    };

    // Handle different navigation types
    const hasLocationChanged =
      previousLocation.current.pathname !== location.pathname ||
      previousLocation.current.search !== location.search ||
      previousLocation.current.hash !== location.hash;

    if (hasLocationChanged || isInitialMount.current) {
      // Always scroll to top on route changes (for non-chat routes)
      handleScrollToTop();

      // Update the previous location
      previousLocation.current = location;
      isInitialMount.current = false;
    }

    // Special handling for back/forward navigation
    if (navigationType === "POP") {
      // Force scroll to top on back/forward navigation
      handleScrollToTop();
    }
  }, [location, navigationType]);

  // Additional popstate event listener for extra safety (but not for chat routes)
  useEffect(() => {
    const handlePopState = (event) => {
      // CRITICAL: Early exit for chat routes
      if (
        window.location.pathname.startsWith("/chat") ||
        window.location.pathname.startsWith("/projects")
      ) {
        return;
      }

      // Prevent any default scroll restoration
      if (event.state) {
        event.preventDefault?.();
      }

      // Force scroll to top
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });

        if (document.documentElement) {
          document.documentElement.scrollTop = 0;
        }

        if (document.body) {
          document.body.scrollTop = 0;
        }
      }, 0);
    };

    window.addEventListener("popstate", handlePopState, { passive: false });

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Prevent any scroll restoration on page unload (but not for chat routes)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // CRITICAL: Early exit for chat routes
      if (
        window.location.pathname.startsWith("/chat") ||
        window.location.pathname.startsWith("/projects")
      ) {
        return;
      }

      // Clear any stored scroll positions
      if (window.history.state) {
        window.history.replaceState(
          { ...window.history.state, scrollPosition: null },
          "",
          window.location.href
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);
}

export default useScrollManager;
